import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowLeft, ChevronRight, Shield } from 'lucide-react';
import { Button } from './ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

gsap.registerPlugin(ScrollTrigger);

const SecurityHero = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      tl.fromTo(
        '.security-hero-line',
        { scaleX: 0, opacity: 0 },
        { scaleX: 1, opacity: 1, duration: 1 }
      );

      tl.fromTo(
        '.security-hero-breadcrumb',
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6 },
        '-=0.6'
      );

      tl.fromTo(
        '.security-hero-title',
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 },
        '-=0.4'
      );

      tl.fromTo(
        '.security-hero-subtitle',
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.7 },
        '-=0.4'
      );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&w=2000&q=80"
          alt="Security camera and access control"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/30" />
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(229,33,51,0.2)_1px,transparent_1px),linear-gradient(90deg,rgba(229,33,51,0.2)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 pt-24 relative z-10">
        <div className="max-w-2xl">
          {/* Breadcrumbs */}
          <nav className="security-hero-breadcrumb flex items-center gap-2 text-white/60 text-sm mb-6">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <ChevronRight className="w-4 h-4" />
            <a href="/services" className="hover:text-white transition-colors">Services</a>
            <ChevronRight className="w-4 h-4" />
            <span className="text-securelec-red">Security</span>
          </nav>

          <div
            className="security-hero-line w-24 h-1 bg-gradient-to-r from-securelec-red to-securelec-red/50 mb-6 origin-left rounded-full"
            style={{ boxShadow: '0 0 20px rgba(229, 33, 51, 0.6)' }}
          />
          <h1 className="security-hero-title text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight drop-shadow-lg">
            Security
          </h1>
          <p className="security-hero-subtitle text-lg md:text-xl text-white/80 leading-relaxed drop-shadow-md">
            Security systems designed, installed and maintained for homes and businesses.
          </p>
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-securelec-light to-transparent" />
    </section>
  );
};

const SecurityContent = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.security-text',
        { x: -60, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      gsap.fromTo(
        '.security-image-block',
        { x: 60, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const services = [
    {
      title: "CCTV",
      description: "CCTV installation provides a complete visual security solution that protects properties by capturing high definition footage, deterring intruders, and giving clients real time visibility from anywhere. The service includes assessing site layout, selecting the right mix of cameras (fixed, PTZ, low light, or AI enabled), and installing them with clean, professional cabling for maximum coverage and reliability. Systems are configured for remote access, motion alerts, and secure recording, ensuring every critical area is monitored around the clock."
    },
    {
      title: "Alarm Systems",
      description: "Installing an alarm system is about delivering a reliable, professionally configured layer of protection that keeps homes and businesses secure around the clock. Our service covers everything from assessing the property layout and identifying vulnerable entry points to selecting the right mix of sensors, control panels, and communication modules. A proper installation ensures clean cabling, optimal sensor placement, seamless integration with mobile apps, and full testing so the system responds instantly to intrusion, tampering, or emergencies."
    },
    {
      title: "Access Control",
      description: "Access control installation delivers a secure, structured way to manage who can enter specific areas of a home, business, or facility. It combines electronic locks, card readers, keypads, intercoms, and management software to ensure only authorised people can access protected zones, while keeping a full audit trail of movements. With proper setup, clients gain a reliable, scalable solution that improves safety, simplifies staff or tenant management, and integrates cleanly with CCTV and alarm systems for a unified security platform."
    },
    {
      title: "Intercom Systems",
      description: "Screen visitors safely before they ever reach your door. We install high-quality audio and video intercoms that allow you to communicate with guests at the gate or front entrance. Whether you need a simple one-to-one villa system or a complex multi-apartment network with smartphone integration, our intercoms add a vital layer of security and convenience to your property."
    },
    {
      title: "Home Automation",
      description: "Home automation installation is the professional integration of internet-connected devices into a unified ecosystem that allows you to manage your home's security, climate, and lighting from a single interface. Rather than managing isolated gadgets, professional installation creates a 'smart' environment where devices communicate and cooperate based on your lifestyle. Features include unified control via mobile app or voice-activated hub, automated 'scenes', remote access, energy efficiency, and hands-free convenience."
    },
    {
      title: "Data & Communication",
      description: "In an era of remote work and high-speed streaming, your network is just as important as your power. We provide professional Cat6 and Cat6A data cabling solutions that outperform patchy Wi-Fi. From installing extra NBN points and server racks to optimising your home office or commercial network, we ensure a seamless, high-bandwidth connection throughout your property."
    },
    {
      title: "Driveway Bollards",
      description: "Protect your property with professionally installed automatic and removable driveway bollards. Ideal for residential driveways, commercial premises, and restricted access areas, bollards provide a strong physical barrier against unauthorised vehicles while maintaining a clean, unobtrusive appearance. We supply and install a range of options including automatic retractable bollards operated by remote control or keypad, heavy-duty fixed bollards for permanent perimeter protection, and removable bollards with lock mechanisms for flexible access control. Our installations include full electrical connection, safety compliance, and integration with existing access control or intercom systems where required."
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-securelec-light relative overflow-hidden">
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-64 bg-gradient-to-b from-transparent via-securelec-red to-transparent" />

      <div className="container mx-auto px-6">
        {/* Back to Services Link */}
        <a 
          href="/what-we-do" 
          className="security-text inline-flex items-center gap-2 text-securelec-gray/60 hover:text-securelec-red transition-colors mb-8 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Services
        </a>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Text Content */}
          <div>
            <span className="security-text inline-flex items-center gap-2 text-securelec-red text-sm font-semibold tracking-widest uppercase mb-4">
              <Shield className="w-4 h-4" />
              What We Offer
            </span>
            <h2 className="security-text text-4xl md:text-5xl font-bold text-securelec-dark mb-6">
              Security Services
            </h2>
            <div className="security-text w-16 h-1 bg-securelec-red mb-8" />
            
            <p className="security-text text-lg text-securelec-gray/80 mb-10 leading-relaxed">
              At Securelec, our security services are delivered by licensed security specialists with extensive experience protecting Melbourne homes and businesses. We don't just install systems – we design tailored solutions that give you genuine peace of mind, backed by professional installation and ongoing support.
            </p>

            {/* Services Accordion */}
            <Accordion type="single" collapsible className="security-text space-y-2">
              {services.map((service, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white rounded-xl border border-gray-200 px-6 overflow-hidden"
                >
                  <AccordionTrigger className="text-xl font-bold text-securelec-dark hover:text-securelec-red hover:no-underline py-5">
                    {service.title}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-securelec-gray/70 leading-relaxed pb-5">
                    {service.description}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {/* Mini CTA */}
            <div className="security-text mt-10">
              <Button
                className="bg-securelec-red hover:bg-securelec-red/90 text-white px-8 py-6 text-lg rounded-md font-semibold relative overflow-hidden group transition-all duration-300 shadow-lg hover:shadow-xl"
                onClick={() => window.location.href = '/get-a-quote'}
              >
                <span className="relative z-10 flex items-center gap-2">
                  Get a Quote
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </Button>
            </div>
          </div>

          {/* Image */}
          <div className="security-image-block relative lg:sticky lg:top-32">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/images/securelec-access-control.webp"
                alt="Securelec access control intercom installed beside a commercial entry"
                className="w-full h-[550px] object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-securelec-dark/20 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-securelec-red/10 rounded-2xl -z-10" />
            <div className="absolute -top-6 -right-6 w-20 h-20 bg-securelec-red/20 rounded-xl -z-10" />
            
            {/* License Badge */}
            <div className="absolute bottom-6 left-6 bg-white rounded-xl p-4 shadow-lg">
              <span className="text-xs text-securelec-gray/50 block">Security License</span>
              <span className="text-lg font-bold text-securelec-red">853-441-92S</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { SecurityHero, SecurityContent };
